import { Cell } from '@pelckmans/elementis/components/table';
import EditIcon from '@pelckmans/elementis/icons/edit';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { RegisterMemberRequestPayload } from '../../../../../../api/my-groups';
import ModalContext from '../../../../../../contexts/ModalContext';
import { buildGroupProps } from '../../../../../../utils/elementisUtils';
import MemberModal, { Member } from '../../member/MemberModal.vo';
import { ImportMembersContext } from '../contexts/ImportMembersContext';
import { linkColumnOptions } from './SelectFieldToLink';

const TRANSLATION_KEY = 'PAGES.MY_GROUPS.MODALS.IMPORT_MEMBERS.VALIDATE_MEMBERS_DATA';

export const SELECTION_COLUMN = 'SELECTION';
export const ACTIONS_COLUMN = 'ACTIONS';
const EDIT_MEMBER_MODAL_ID = 'edit-member-modal';

export const COLUMN_ORDER = [
  SELECTION_COLUMN,
  linkColumnOptions.FIRST_NAME,
  linkColumnOptions.LAST_NAME,
  linkColumnOptions.PASSWORD,
  linkColumnOptions.USERNAME,
  linkColumnOptions.EMAIL,
  ACTIONS_COLUMN,
] as const;

interface Props {
  rowIndex: number;
  member: RegisterMemberRequestPayload;
}

export default function MemberDataRow({ rowIndex, member }: Props) {
  const { t } = useTranslation();
  const { showModal } = useContext(ModalContext);

  const { selection, setSelection, errors, toBeAddedMembers, setToBeAddedMembers, existingUsernames } =
    useContext(ImportMembersContext);

  function handleSelect(index: number) {
    setSelection(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(index)) {
        newSelection.delete(index);
      } else {
        newSelection.add(index);
      }
      return newSelection;
    });
  }

  function onMemberEdited(rowIndex: number) {
    return (member: Member) => {
      setToBeAddedMembers(prev => {
        const updated = [...prev];
        updated[rowIndex] = {
          firstName: member.firstName!,
          lastName: member.lastName!,
          username: member.username!,
          password: member.password!,
          ...(member.primaryEmail ? { email: member.primaryEmail } : {}),
        };
        return updated;
      });
    };
  }

  function handleEditMember(rowIndex: number) {
    const allUsernames = toBeAddedMembers.map(member => member.username || '').concat(existingUsernames);

    showModal(
      <MemberModal
        id={EDIT_MEMBER_MODAL_ID}
        currentMember={mapToMember(member)}
        onMemberEdited={onMemberEdited(rowIndex)}
        existingUsernames={allUsernames}
      />,
    );
  }

  return (
    <tr>
      {COLUMN_ORDER.map(columnOption => {
        const key = `cell-${columnOption}-${rowIndex}`;
        const error = errors?.[`${rowIndex}`] ? true : undefined;

        if (columnOption === SELECTION_COLUMN) {
          return (
            <Cell density="compact" key={key} error={error} truncated={false} sticky>
              <Cell.Selection
                checked={selection.has(rowIndex) ? true : undefined}
                onChange={() => handleSelect(rowIndex)}
                error={error}
              />
            </Cell>
          );
        }

        if (columnOption === ACTIONS_COLUMN) {
          return (
            <Cell
              density="compact"
              key={key}
              error={error}
              buttonGroupProps={buildGroupProps(<></>)}
              truncated={false}
              sticky
            >
              <Cell.Button
                prominence="neutral"
                startIcon={<EditIcon />}
                aria-label={t(`${TRANSLATION_KEY}.TABLE.ACTIONS.EDIT.A11Y_LABEL`)}
                onClick={() => handleEditMember(rowIndex)}
              />
            </Cell>
          );
        }

        const emailColumn = columnOption === linkColumnOptions.EMAIL;

        return (
          <Cell
            density="compact"
            key={key}
            error={error}
            truncated={false}
            minWidth={emailColumn ? 'md' : 'sm'}
            maxWidth={emailColumn ? 'md' : 'sm'}
          >
            <Cell.Text title={getMemberData(member, columnOption)} />
          </Cell>
        );
      })}
    </tr>
  );
}

function getMemberData(member: RegisterMemberRequestPayload, column: string) {
  switch (column) {
    case linkColumnOptions.FIRST_NAME:
      return member.firstName;
    case linkColumnOptions.LAST_NAME:
      return member.lastName;
    case linkColumnOptions.PASSWORD:
      return member.password;
    case linkColumnOptions.USERNAME:
      return member.username;
    case linkColumnOptions.EMAIL:
      return member.email || '';
  }

  return '';
}

function mapToMember(member: RegisterMemberRequestPayload): Member {
  return {
    firstName: member.firstName,
    lastName: member.lastName,
    primaryEmail: member.email || '',
    username: member.username,
    password: member.password,
  };
}
